import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { MessageSquare, Bell, AlertTriangle, ClipboardList, Save, RefreshCw, CheckCircle, Phone, Info } from 'lucide-react';

const DEFAULT_PREFS = {
  new_applications: {
    enabled: false,
    phone: '',
    threshold: 1,
    label: 'Nouvelles Candidatures',
    description: 'Recevoir un SMS à chaque nouvelle candidature professionnelle soumise.',
    icon: ClipboardList,
    color: 'blue'
  },
  system_alerts: {
    enabled: false,
    phone: '',
    label: 'Alertes Système',
    description: 'Recevoir un SMS lors d\'erreurs critiques ou d\'anomalies système détectées.',
    icon: AlertTriangle,
    color: 'red'
  },
  approval_queue_overload: {
    enabled: false,
    phone: '',
    threshold: 10,
    label: 'Surcharge File d\'Approbation',
    description: 'Recevoir un SMS quand la file d\'approbation dépasse le seuil configuré.',
    icon: Bell,
    color: 'orange'
  }
};

const COLOR_MAP = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
    toggle: 'bg-blue-600'
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    badge: 'bg-red-100 text-red-700',
    toggle: 'bg-red-600'
  },
  orange: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'text-orange-600',
    badge: 'bg-orange-100 text-orange-700',
    toggle: 'bg-orange-500'
  }
};

const SmsNotificationPreferences = () => {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const SETTINGS_KEY = 'admin_sms_notification_preferences';

  const fetchPrefs = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        ?.from('admin_settings')
        ?.select('value')
        ?.eq('key', SETTINGS_KEY)
        ?.maybeSingle();

      if (fetchError && fetchError?.code !== 'PGRST116') throw fetchError;

      if (data?.value) {
        setPrefs(prev => {
          const merged = { ...prev };
          Object.keys(prev)?.forEach(k => {
            if (data?.value?.[k]) {
              merged[k] = { ...prev?.[k], ...data?.value?.[k] };
            }
          });
          return merged;
        });
      }
    } catch (err) {
      // Table may not exist yet — use defaults silently
      console.warn('SMS prefs fetch:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrefs();
  }, []);

  const handleToggle = (key) => {
    setPrefs(prev => ({
      ...prev,
      [key]: { ...prev?.[key], enabled: !prev?.[key]?.enabled }
    }));
    setSaved(false);
  };

  const handleChange = (key, field, value) => {
    setPrefs(prev => ({
      ...prev,
      [key]: { ...prev?.[key], [field]: value }
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Validate phone numbers for enabled events
      for (const [key, pref] of Object.entries(prefs)) {
        if (pref?.enabled && !pref?.phone?.trim()) {
          setError(`Veuillez saisir un numéro de téléphone pour "${pref?.label}".`);
          setSaving(false);
          return;
        }
        if (pref?.enabled && pref?.phone?.trim() && !/^\+?[\d\s\-()]{8,20}$/?.test(pref?.phone?.trim())) {
          setError(`Numéro de téléphone invalide pour "${pref?.label}". Utilisez le format international (ex: +225 07 00 00 00 00).`);
          setSaving(false);
          return;
        }
      }

      // Serialize only the fields we need to persist
      const toSave = {};
      Object.keys(prefs)?.forEach(k => {
        const { enabled, phone, threshold } = prefs?.[k];
        toSave[k] = { enabled, phone: phone?.trim() || '', ...(threshold !== undefined ? { threshold } : {}) };
      });

      const { error: upsertError } = await supabase
        ?.from('admin_settings')
        ?.upsert({ key: SETTINGS_KEY, value: toSave, updated_at: new Date()?.toISOString() }, { onConflict: 'key' });

      if (upsertError) throw upsertError;

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save SMS prefs:', err);
      setError('Erreur lors de la sauvegarde. Vérifiez que la table admin_settings existe.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Préférences Notifications SMS</h2>
              <p className="text-sm text-gray-500">Configurez les alertes SMS pour les événements critiques du système</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchPrefs}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Actualiser</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : saved ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{saving ? 'Sauvegarde...' : saved ? 'Sauvegardé !' : 'Sauvegarder'}</span>
            </button>
          </div>
        </div>

        {/* Info banner */}
        <div className="mt-4 flex items-start space-x-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
          <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Les SMS sont envoyés via le service de messagerie configuré. Assurez-vous que les numéros sont au format international (ex: <strong>+225 07 00 00 00 00</strong>). Les préférences sont sauvegardées dans la table <code className="bg-blue-100 px-1 rounded">admin_settings</code>.
          </p>
        </div>

        {error && (
          <div className="mt-3 flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
      {/* Event Cards */}
      <div className="grid grid-cols-1 gap-5">
        {Object.entries(prefs)?.map(([key, pref]) => {
          const colors = COLOR_MAP?.[pref?.color] || COLOR_MAP?.blue;
          const EventIcon = pref?.icon;
          return (
            <div
              key={key}
              className={`bg-white rounded-xl shadow-sm border ${pref?.enabled ? colors?.border : 'border-gray-200'} transition-all duration-200`}
            >
              <div className="p-6">
                {/* Card header */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start space-x-4">
                    <div className={`w-10 h-10 ${colors?.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <EventIcon className={`w-5 h-5 ${colors?.icon}`} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                        <h3 className="font-semibold text-gray-900">{pref?.label}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pref?.enabled ? colors?.badge : 'bg-gray-100 text-gray-500'}`}>
                          {pref?.enabled ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{pref?.description}</p>
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(key)}
                    className="flex-shrink-0 focus:outline-none"
                    aria-label={pref?.enabled ? 'Désactiver' : 'Activer'}
                  >
                    <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${pref?.enabled ? colors?.toggle : 'bg-gray-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${pref?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </div>
                  </button>
                </div>

                {/* Expanded config when enabled */}
                {pref?.enabled && (
                  <div className={`mt-5 pt-5 border-t ${colors?.border} space-y-4`}>
                    {/* Phone number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <span className="flex items-center space-x-1.5">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>Numéro de téléphone destinataire</span>
                        </span>
                      </label>
                      <input
                        type="tel"
                        value={pref?.phone || ''}
                        onChange={(e) => handleChange(key, 'phone', e?.target?.value)}
                        placeholder="+225 07 00 00 00 00"
                        className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-400 mt-1">Format international requis (ex: +225 07 12 34 56 78)</p>
                    </div>

                    {/* Threshold (for new_applications and approval_queue_overload) */}
                    {pref?.threshold !== undefined && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {key === 'new_applications' ?'Envoyer un SMS toutes les N nouvelles candidatures' :'Seuil de surcharge (nombre de candidatures en attente)'}
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min={1}
                            max={key === 'approval_queue_overload' ? 500 : 50}
                            value={pref?.threshold}
                            onChange={(e) => handleChange(key, 'threshold', parseInt(e?.target?.value, 10) || 1)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <span className="text-sm text-gray-500">
                            {key === 'new_applications'
                              ? `SMS envoyé toutes les ${pref?.threshold} candidature${pref?.threshold > 1 ? 's' : ''}`
                              : `Alerte si ≥ ${pref?.threshold} candidature${pref?.threshold > 1 ? 's' : ''} en attente`}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className={`${colors?.bg} rounded-lg p-3`}>
                      <p className="text-xs font-medium text-gray-600 mb-1">Aperçu du message SMS :</p>
                      <p className="text-xs text-gray-700 font-mono">
                        {key === 'new_applications' && `[KapitalEstates] 🏠 Nouvelle candidature professionnelle reçue. Consultez le tableau de bord admin pour révision.`}
                        {key === 'system_alerts' && `[KapitalEstates] ⚠️ Alerte système critique détectée. Vérifiez immédiatement le tableau de bord admin.`}
                        {key === 'approval_queue_overload' && `[KapitalEstates] 🔔 File d'approbation surchargée (${pref?.threshold}+ candidatures en attente). Action requise.`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Summary footer */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Résumé des notifications actives</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(prefs)?.map(([key, pref]) => {
            const colors = COLOR_MAP?.[pref?.color] || COLOR_MAP?.blue;
            return (
              <span
                key={key}
                className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${pref?.enabled ? colors?.badge : 'bg-gray-100 text-gray-400'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${pref?.enabled ? 'bg-current' : 'bg-gray-300'}`} />
                <span>{pref?.label}</span>
              </span>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          {Object.values(prefs)?.filter(p => p?.enabled)?.length} sur {Object.keys(prefs)?.length} événements activés
        </p>
      </div>
    </div>
  );
};

export default SmsNotificationPreferences;
